// diamond/tokens.js — Diamond UI's design foundation.
//
// This is Foundation item #1 and #2 from the Diamond UI build list: one
// enumerated z-index scale (Glass UI's ad-hoc values ranged from single
// digits to 999999, tracked only by inline comments) and one theming
// mechanism (Glass UI split between a JS-object-via-inline-styles pattern
// and a CSS-custom-properties pattern used by a sibling surface — this
// file is the single source of truth for Diamond UI specifically).
//
// Kept as a plain JS export (not CSS custom properties) deliberately: every
// existing swipe3/swipe2 sibling in this codebase consumes its theme as a
// `T` tokens object passed as a prop, and Diamond UI's components are new
// but still live in the same render tree / same inline-style convention as
// everything around them. Re-basing to CSS variables is a real, separable
// upgrade the build list calls out — not blocking this first slice.

// ── Z-Index scale ────────────────────────────────────────────────────────
// One enumerated scale, six rungs. Every Diamond UI surface picks from this
// object by name — nothing in Diamond UI should ever write a raw z-index
// number.
export const Z = {
  base: 0,
  content: 10,
  header: 20,
  scrim: 30,
  overlay: 31,      // drawers/panels that sit on the scrim
  popover: 40,       // small anchored popovers (reaction picker, mention list)
  modal: 50,         // centered modals (position detail, quick trade)
  toast: 60,         // celebration overlays, mention-jump pill
  portalMax: 70,      // anything portaled to document.body to escape a
                      // backdrop-filter containing block — still just "70",
                      // not a ceiling-busting 999999, because nothing else
                      // in Diamond UI is allowed to exceed this scale either.
};

// ── Type scale ───────────────────────────────────────────────────────────
export const TYPE = {
  display: 26,
  title: 18,
  body: 14,
  label: 12,
  caption: 10.5,
  mono: "'IBM Plex Mono', ui-monospace, 'SF Mono', monospace",
  sans: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

// ── Spacing scale (px) ───────────────────────────────────────────────────
export const SPACE = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 };

// ── Radius scale ─────────────────────────────────────────────────────────
// Diamond's own visual signature: precise, cut-facet corners rather than
// the soft all-pill-everything look — smaller radii than Glass UI used,
// consistently applied by role (card vs. pill vs. input), not per-component
// guesswork.
export const RADIUS = { sm: 8, md: 12, lg: 16, pill: 999 };

// ── Theme tokens ─────────────────────────────────────────────────────────
// Semantic colors stay consistent with the rest of the real app (green/red
// for P&L, gold for zBucks, purple for Omega-AI-branded surfaces, blue
// accent) — this is the actual product, not a fresh brand exercise, so
// continuity with what a user already reads as "gain" or "zBucks"
// elsewhere in the app matters more than novelty here.
export const THEME = {
  dark: {
    bgBase: "#08090d",
    surface: "#111318",
    surface2: "#171a21",
    surface3: "#1e222b",
    border: "rgba(255,255,255,0.08)",
    borderStrong: "rgba(255,255,255,0.14)",
    text: "#f2f4f8",
    sub: "#aeb4c2",
    muted: "#767d8c",
    faint: "#4a5061",
    accent: "#5b8cff",
    accentSoft: "rgba(91,140,255,0.14)",
    green: "#2fd88f",
    greenSoft: "rgba(47,216,143,0.13)",
    red: "#ff5c72",
    redSoft: "rgba(255,92,114,0.13)",
    gold: "#f2b90c",
    goldSoft: "rgba(242,185,12,0.13)",
    purple: "#a78bfa",
    purpleSoft: "rgba(167,139,250,0.13)",
    orange: "#f97316",
    orangeSoft: "rgba(249,115,22,0.13)",
    shadow: "0 1px 2px rgba(0,0,0,0.35), 0 12px 32px rgba(0,0,0,0.45)",
    scrim: "rgba(3,4,7,0.62)",
  },
  light: {
    bgBase: "#f4f5f8",
    surface: "#ffffff",
    surface2: "#eef0f4",
    surface3: "#e4e7ee",
    border: "#dde1e9",
    borderStrong: "#c7ccd8",
    text: "#12151c",
    sub: "#4c525f",
    muted: "#767d8c",
    faint: "#9aa0ad",
    accent: "#3457d5",
    accentSoft: "rgba(52,87,213,0.10)",
    green: "#16875a",
    greenSoft: "rgba(22,135,90,0.10)",
    red: "#c22c47",
    redSoft: "rgba(194,44,71,0.10)",
    gold: "#96700a",
    goldSoft: "rgba(150,112,10,0.10)",
    purple: "#6d4fd6",
    purpleSoft: "rgba(109,79,214,0.10)",
    orange: "#b3540d",
    orangeSoft: "rgba(179,84,13,0.10)",
    shadow: "0 1px 2px rgba(15,18,25,0.05), 0 8px 24px rgba(15,18,25,0.07)",
    scrim: "rgba(20,22,30,0.42)",
  },
};

// Compatibility aliases — several components mounted inside Diamond UI
// (TraderCardControl, CapitalPanel, AgentDetailCard, FloorGoalControl,
// GovernanceModeControl, TraderOfTheDay) are shared with Glass UI and read
// swipe3/shared.js's THEME key names directly, not Diamond's own. Without
// these, those reads silently resolve to undefined — a dropped CSS
// property, not a crash — which is invisible in dark mode (where the
// fallback colors those components hardcode happen to already be dark) but
// breaks light mode outright (a hardcoded-dark panel with dark text on it).
// Derived from the canonical keys above so they can't drift out of sync.
for (const t of Object.values(THEME)) {
  t.glass = t.surface;
  t.glassStrong = t.surface2;
  t.glassBorder = t.border;
  t.hair = t.border;
  t.card = t.surface;
  t.drawer = t.bgBase;
  t.overlay = t.scrim;
  t.yellow = t.gold;
}

// ── Theme resolution — same "auto = market hours" rule Glass UI uses, kept
// consistent so switching between UI modes doesn't flip the user's theme
// out from under them. ─────────────────────────────────────────────────
// Identical to swipe3/shared.js's by construction now — both re-export the
// one clock in lib/marketClock.js, so every UI mode resolves "auto" the
// same way (holidays and early closes included).
export { resolveThemeChoice } from "../../lib/marketClock";

// ── Semantic status colors — resolved against the active theme. ────────────
// AgentSheet, LeaderboardPanel, and SettingsPanel each used to keep their
// own file-scope object of raw hex for these exact same statuses (e.g.
// freedom/standard/defense tier color typed out twice, byte-for-byte, in
// two files). Two problems with that: those hex values only matched the
// DARK theme's green/accent/red/gold, so a light-mode badge quietly kept
// rendering dark-mode colors right next to correctly-themed text; and two
// independent copies of the same mapping can drift the next time someone
// edits one and not the other. These functions are the one place every
// status badge should read its color from instead.
export function tierColor(T, tier) {
  return { freedom: T.green, standard: T.accent, defense: T.red }[tier] || T.accent;
}
export function riskTierColor(T, tier) {
  return { trusted: T.green, hot_streak: T.gold, standard: T.accent, cautious: T.orange, cold_streak: T.red }[tier] || T.accent;
}
export function goalStatusColor(T, status) {
  return { ahead: T.green, on_pace: T.accent, behind: T.orange, off_track: T.red, achieved: T.gold }[status] || T.accent;
}
export function riskModeColor(T, id) {
  return { med: T.green, moderate: T.accent, high: T.gold, ai_auto: T.purple }[id] || T.accent;
}
export function autopilotModeColor(T, id) {
  return { FULL_AUTO: T.red, ASSISTED: T.gold, OBSERVE_ONLY: T.accent }[id] || T.accent;
}

// Small glass-surface helper — used sparingly (floating chrome only, never
// full-screen ambient glow) per the session's own "amplify vs. clutter"
// lesson from tonight's mockup work.
export function glass(T, extra = {}) {
  return {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.lg,
    ...extra,
  };
}

// Diamond's one active-state signature: a soft halo, used consistently on
// every selected pill/tab/toggle/chip in the app instead of each surface
// inventing its own. Defaults to accent blue; pass `color` to match a chip
// that already carries its own semantic color (e.g. the green Trades chip)
// rather than forcing blue onto it. `strength` 1 = default; turn it up for
// a single hero focal point (e.g. a just-toggled switch), never for more
// than one element on screen at a time.
export function glow(T, strength = 1, color = null) {
  const c = color || T.accent;
  // The alpha suffixes below (e.g. "55") are only valid appended to a
  // 6-digit hex color — silently produces a broken (dropped) boxShadow for
  // anything else, like an rgba() token (T.accentSoft). Fall back to a
  // flat-opacity shadow for those instead of failing silently.
  if (!/^#[0-9a-fA-F]{6}$/.test(c)) {
    return { boxShadow: `0 0 ${10 * strength}px ${c}, 0 0 ${2 * strength}px ${c}` };
  }
  return { boxShadow: `0 0 ${10 * strength}px ${c}${strength >= 1.5 ? "88" : "55"}, 0 0 ${2 * strength}px ${c}aa` };
}
